<script>
import { fandomBasicMixin } from 'src/modules/fandom_mixins_module.js';
import { pick, get } from "lodash";
import Flickity from "flickity";

const getTotalLessons = chapters => chapters.map(item => item.chapters.length).reduce((i, acc) => acc + i, 0);

export default {
  mixins: [fandomBasicMixin],
  data() {
    return {
    	bookmark: this.content.chapters[0].chapters[0],
      sidebarOpen: false,
      unitNumber: 1,
      videoOnPlay: false,
      accessoriesCarousel: null,
    };
  },
  mounted() {
    Vue.nextTick(() => {
      this.initialBookmark();
      this.initAccessoriesCarousel();
    });
    Fandom.onEvent("player-status-changed", ({ containerContentId, $id, playing }) => {
      if (this.containerContent.id === containerContentId && this.bookmark.$id === $id) {
        this.videoOnPlay = playing;
      }
    });
  },
  watch: {
    bookmark(newValue, _) {
      this.$refs["course-sidebar"].bookmark = newValue;
      if (!!this.accessoriesCarousel) {
        this.accessoriesCarousel.destroy();
      }
      this.initAccessoriesCarousel();
    },
    unitNumber(newValue, previousValue) {
      this.$refs["course-sidebar"].unitNumber = newValue;
    }
  },
  computed: {
    showSidebar() {
      return this.sidebarOpen && !this.videoOnPlay;
    },
    showCollapsedSidebar() {
      return !(this.sidebarOpen || this.videoOnPlay);
    },
    chapters() {
      return get(this, ["content", "chapters"], []);
    },
    lessons() {
      return getTotalLessons(this.chapters);
    },
    selectedUnit() {
      return this.chapters[this.unitNumber - 1];
    },
    activeIndex() {
      const chapters = get(this.selectedUnit, "chapters", []);
      const index = chapters.findIndex(chapter => {
        return chapter.$id === this.bookmark.$id;
      });
      return index + 1;
    },
    selectedLessonIndex() {
      const index = this.unitNumber - 1;
      const previousChapters = this.chapters.slice(0, index - 1);
      return getTotalLessons(previousChapters) + this.activeIndex;
    },
    share() {
      return get(this, ["content", "interactions", "share"], false);
    },
    owner() {
      const owner = get(this, ["content", "owned_by"], false);
      return get(this, ["pageModel", "id_to_user", owner], false);
    },
    difficulty() {
      const difficulty = get(this, ["content", "info", "difficulty"], false);
      const content = difficulty ? this.getContent(difficulty) : false;
      return content ? content.title : false;
    },
    lastBookmark() {
      return this.unitNumber === this.chapters.length && this.activeIndex === this.selectedUnit.chapters.length;
    },
    relatedsExtraField() {
      return Fandom.exists(this.content["related_content_course_tag"]) ? "related_content_course_tag" : "category";
    },
    accessories() {
      return this.getTagsInfoFromContents("accessories", ["slug","thumbnail","title"]);
    },
    canGoNext() {
      return (this.activeIndex < this.selectedUnit.chapters.length || this.unitNumber < this.chapters.length);
    },
    canGoPrev() {
      const index = this.activeIndex - 1;
      const unit = this.unitNumber - 1;
      return (index > 0 || unit > 0);
    }
  },
  methods: {
    getBackgroundUrl: Fandom.getBackgroundUrl,
    initAccessoriesCarousel() {
      Vue.nextTick(() => {
        if (Fandom.exists(this.bookmark.accessories)) {
          this.accessoriesCarousel = new Flickity(this.$refs["accessory-carousel"], {
            draggable: ">1",
            prevNextButtons: false,
            pageDots: false,
            cellAlign: "left",
            wrapAround: false,
            freeSCroll: false,
            contain: true
          });
        }
      })
    },
    initialBookmark() {
      const location = window.location.href;
      const bookmarkAnchorPrefix = `bookmark-${this.containerContent.id}-`;
      if (location.includes(`#${bookmarkAnchorPrefix}`)) {
        const anchorChapterId = location.split("#")[1].replace(bookmarkAnchorPrefix, "");
        let tmpUnitNumber = 1;

        this.chapters.forEach(chapter => {
          chapter.chapters.forEach(chapter => {
            if (chapter.$id == anchorChapterId) {
              this.bookmark = chapter;
              this.unitNumber = tmpUnitNumber;
            }
          });
          tmpUnitNumber++;
        });
      }
    },
    getTagsInfoFromContents(name, info = "title") {
      const field = get(this.bookmark, name, []);
      const arrayConverter = content => Fandom.exists(content) ? pick(content, info) : false;
      const baseConverter = content => Fandom.exists(content) ? content[info] : false;
      const converter = Array.isArray(info) ? arrayConverter : baseConverter;
      return field.map(this.getContent).map(converter);
    },
  	setBookmark(data) {
  		this.bookmark = data.chapter;
      this.unitNumber = data.unitNumber;
      if (this.sidebarOpen) {
        this.toggleSidebar();
      }
  	},
    toggleSidebar() {
      if (!this.sidebarOpen) {
        this.sidebarOpen = true;
        Vue.nextTick(() => {
          this.$refs["course-sidebar"].$emit("scroll-lock");
        });
      } else {
        this.sidebarOpen = false;
        this.$refs["course-sidebar"].$emit("scroll-unlock");
      }
    },
    goToNextBookmark() {
      if (this.activeIndex < this.selectedUnit.chapters.length) {
        this.bookmark = this.selectedUnit.chapters[this.activeIndex];
      } else if (this.unitNumber < this.chapters.length) {
        this.bookmark = this.chapters[this.unitNumber].chapters[0];
        this.unitNumber +=1;
      } else {
        /* do nothing */
      }
    },
    goToPrevBookmark() {
      const index = this.activeIndex - 1;
      const unit = this.unitNumber - 1;
      if (index > 0) {
        this.bookmark = this.selectedUnit.chapters[index - 1];
      } else if (unit > 0) {
        this.bookmark = this.chapters[unit - 1].chapters[this.chapters[unit + 1].length - 1];
        this.unitNumber -= 1;
      } else {
        /* do nothing */
      }
    }
  }
};
</script>

<template>
  <div v-easyadmin="easyadminId" :class="contentType" course-product-component>
    <div class="container">
      <div class="row">
        <div class="col-12 col-lg-8 col-xl-7 pl-lg-0">
          <div class="row">
            <div class="col-12 px-0 pt-3 pt-lg-0">
              <breadcrumb class="px-0"></breadcrumb>
            </div>
          </div>
        </div>
      </div>
    </div>
    <div class="container-fluid">
      <div class="row">
        <div class="col-md-auto px-0">
          <transition
            enter-active-class="animated faster slideInLeft"
            leave-active-class="animated faster slideOutLeft"
          >
            <div class="sidebar h-100 mw-100 bg-kenwood-light-grey fixed-top" ref="sidebar" v-show="showSidebar">
              <course-sidebar
                class="w-100"
                ref="course-sidebar"
                :content="content"
                :propBookmark="bookmark"
                @set-bookmark="setBookmark"
                @toggle-sidebar="toggleSidebar()"
                >
              </course-sidebar>
            </div>
          </transition>
          <div class="sidebar-toggler" v-show="showCollapsedSidebar">
            <div class="zindex-modal top-0 py-3 px-4 m-3 position-absolute d-flex align-items-center bg-kenwood-light-grey cursor-pointer" @click="toggleSidebar">
              <i class="fal fa-list-ul"></i>
              <span class="pl-2 text-uppercase">{{ft('course.contents')}}</span>
            </div>
          </div>
        </div>
        <div class="col-12 col-md px-0">
          <div class="container px-0">
            <div
              v-if="bookmark.layout"
              :is="bookmark.layout"
              v-on:next-bookmark="goToNextBookmark"
              :content="bookmark"
              :lastBookmark="lastBookmark"
              :opt-container-content="containerContent"
              :animation="false"
              :unitNumber="unitNumber"
              :key="bookmark.$id"
              bookmark
            ></div>
          </div>
        </div>
      </div>
    </div>
    <div class="container">
      <div class="row border-bottom">
        <div class="col-12 d-flex justify-content-between my-4">
          <button class="btn btn-rhubarb px-4 py-3 mr-3" @click="goToPrevBookmark()" :disabled="!canGoPrev"><i class="far fa-chevron-left"></i> <span class="d-none d-md-inline">{{ft('course.lesson')}}</span> {{ft('course.previous')}}</button>
          <button class="btn btn-rhubarb px-4 py-3" @click="goToNextBookmark()" :disabled="!canGoNext"><span class="d-none d-md-inline">{{ft('course.lesson')}}</span> {{ft('course.next')}} <i class="far fa-chevron-right"></i></button>
        </div>
      </div>
    </div>
    <div class="container">
      <div class="row border-bottom">
        <div class="col-12 col-lg-8 col-xl-7 pt-3 pt-lg-5">
          <div class="row">
            <div class="col-12 col-lg-8 col-xl-7 pl-lg-0 pb-3">
              <p class="text-uppercase font-sm">{{ft('course.lesson')}} {{selectedLessonIndex}} - {{content.title}}</p>
              <div v-if="bookmark.title_composed" class="title-composed">
                <h5 v-for="(titleLine, index) in bookmark.title_composed" :key="`bookmark-title-${index}`" :class="getTitleStyle(titleLine)" class="d-inline-block h5 m-0 titleline">
                  {{titleLine.title}}
                </h5>
              </div>
              <h5 class="pb-3 title-composed-fallback" v-else-if="bookmark.title">{{bookmark.title}}</h5>
              <p class="pt-2" v-if="bookmark.description" v-html="bookmark.description"></p>
            </div>
          </div>
        </div>
        <div class="col-12 col-lg-4 col-xl-5 pt-3 pt-lg-5">
          <div class="pb-3 mb-2 d-flex align-items-center pl-0 col-12" v-if="owner">
            <div v-if="owner.avatar" class="owner-avatar border-rounded bg-cover-center" :style="{'background-image': getBackgroundUrl(owner.avatar.url)}"></div>
            <span class="pl-2 font-sm">By <b>{{owner.first_name}} {{owner.last_name ? `${owner.last_name[0]}.` : ''}}</b></span>
          </div>
          <div v-if="content.info" class="col-12 pb-3 text-uppercase flex-wrap font-xs pl-0 d-flex align-items-center">
            <div v-if="difficulty" class="pr-4">
              {{ft("course.difficulty")}}:
              <span class="text-rhubarb">{{difficulty}}</span>
            </div>
            <div v-if="content.info.quantity" class="pr-4">
              <span class="text-rhubarb">{{content.info.quantity}}</span>
            </div>
            <div v-if="lessons > 0" class="pr-4">
              {{ft("course.lessons")}}:
              <span class="text-rhubarb">{{lessons}}</span>
            </div>
          </div>
          <div class="text-uppercase font-xs d-flex pb-4" v-if="share">
            <div class="d-flex align-items-center font-sm pr-3">{{ft("course.share_course")}}:</div>
            <share-icons :content="content" :containerContent="containerContent"></share-icons>
          </div>
        </div>
      </div>
      <div class="row">
        <div class="col-12 pl-lg-0 border-bottom py-4 py-md-5" v-if="accessories.length > 0">
          <h4 class="pb-3">{{ft("course.equipment")}}</h4>
          <div class="accessory-carousel" ref="accessory-carousel">
            <div class="accessory-carousel-item border border-rhubarb" v-for="(accessory, index) in accessories" :key="`carousel-slide-${index}`">
              <a :href="accessory.slug">
                <div v-if="accessory.thumbnail" class="w-100 h-100 position-absolute bg-cover-center" :style="{'background-image': getBackgroundUrl(accessory.thumbnail.url)}">
                </div>
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
    <div class="w-100">
      <component :is="stripe.layout" :content="stripe" v-for="(stripe, index) in bookmark.stripes" :key="stripe.$id" :opt-container-content="containerContent"></component>
      <component :is="stripe.layout" :content="stripe" v-for="(stripe, index) in content.stripes" :key="stripe.$id" :opt-container-content="containerContent" class="bookmark__footer"></component>
      <stripe-related-courses
        :content="content"
        :opt-container-content="optContainerContent"
        :tagExtraField="relatedsExtraField"
        :optionalRelatedContentNames="content.related_content_courses"
        :optionalViewMoreLink="(content.related_view_more_link || {}).url"
        ></stripe-related-courses>
    </div>
    <transition enter-active-class="animated faster fadeIn" leave-active-class="animated faster fadeOut">
      <div class="sidebar-shadow zindex-modal w-100 h-100 fixed-top" v-if="sidebarOpen" @click="toggleSidebar()"></div>
    </transition>
  </div>
</template>

<style lang="scss" scoped>
.zindex-modal {
  z-index: $zindex-modal;
  border: 1px solid #dee2e6 !important;
}

.top-0 {
  top: 0;
}

.title-composed,
.title-composed-fallback {
  color: $kenwood-dark-grey;
  font-family: $bressay-font;
}

.titleline {
  & + & {
    margin-left: .5ch !important;
  }
}

.sidebar {
  width: 85%;
  z-index: $zindex-modal + 1;

  @include media-breakpoint-up(sm) {
    width: 26.5rem;
  }
}

.sidebar-toggler {
  @include media-breakpoint-up(md) {
    z-index: $zindex-sticky;
    position: sticky;
    top: 0;
  }
  @include media-breakpoint-down(sm) {
    z-index: $zindex-sticky;
    position: fixed;
    bottom: 90px;
  }
}

.owner-avatar {
  height: 2.625rem;
  width: 2.625rem;
}

.sidebar-shadow {
  background-color: rgba($black, .3);
}

[stripe-lesson-component] {
  & + & {
    border-top: 1px solid $border-color;
  }
}

.accessory-carousel-item {
  width: 15.16666%;
  padding-bottom: 15.16666%;
  margin-right: 1.5%;

  @include media-breakpoint-down(sm) {
    width: 38.5%;
    padding-bottom: 38.5%;
    margin-right: 1.5%;
  }
}
</style>
